import React, { useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { useLocations } from "../../hooks/locations";
import { useTranslation } from "react-i18next";
import Table from "../../components/ReactTable/Table";
import TextFilter from "../../components/ReactTable/Filters/TextFilter";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import LocationsFilters from "./LocationsFilters";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { buildLocationInformationRoute } from "../../routes/AdminRoutes";
import { useReport } from "../../hooks/useReport";
import { useTableData } from "../../components/ReactTable/useTableData";
import useConstant from "use-constant";
import { debounce } from "lodash";
import TooltipButton from "../../components/Buttons/TooltipButton";
import usePermissions from "../../redux/selectors/usePermissions";
import useUser from "../../hooks/useUser";

const FullLocation = ({ row }) => {
  return (
    <>
      {row.city}
      {row.state ? `, ${row.state}` : ""}
    </>
  );
};

const columns = (t, isClient) => {
  const divisionColumn = {
    Header: t("table.columnHeader.OwnedBy"),
    accessor: "customer.name",
    Filter: TextFilter,
    canSort: true,
    filterPlaceholder: t("table.filterPlaceholder.ownedByAll"),
    id: "divisionName"
  };
  const columns = [
    {
      Header: t("locations.name"),
      accessor: "name",
      Filter: TextFilter,
      filterPlaceholder: t("locations.name"),
      canSort: true,
      id: "name"
    },
    {
      Header: t("locations.fullLocation"),
      accessor: row => <FullLocation row={row} />,
      Filter: TextFilter,
      canSort: true,
      filterPlaceholder: t("locations.fullLocation"),
      id: "city"
    },
    {
      Header: t("locations.employeeCount"),
      accessor: "employeeCount",
      Filter: TextFilter,
      canSort: true,
      filterPlaceholder: t("locations.employeeCount"),
      id: "usersCount"
    }
  ];
  isClient && columns.push(divisionColumn);
  return columns;
};

const Locations = () => {
  const { getLocations } = useLocations();
  const { isGoldOrPlatinum } = usePermissions();
  const { isClient } = useUser();
  const dispatch = useDispatch();
  const {
    data,
    paginationState,
    onStateChange,
    onApplyExternalFilters,
    isFetching
  } = useTableData(getLocations);
  const { t } = useTranslation();

  const handleRowClick = row => {
    dispatch(push(buildLocationInformationRoute(row.id)));
  };

  const openReport = useReport("Training Assignment by Location Report");
  const memoColumns = useMemo(() => columns(t, isClient), []);

  const handleStateChange = newState => {
    onStateChange(newState);
  };

  const onFilterLocations = useConstant(() =>
    debounce(onApplyExternalFilters, 500)
  );

  const submitExternalFilter = useCallback(
    (exFilters, { setSubmitting }) => {
      onFilterLocations(exFilters);
      setSubmitting(false);
    },
    [onFilterLocations]
  );

  return (
    <AdminPageLayout
      title="Locations"
      headerActions={
        <>
          {openReport && (
            <TooltipButton
              color="primary"
              variant="contained"
              onClick={openReport}
              tooltipProps={{
                title: isGoldOrPlatinum
                  ? ""
                  : t("administration.goldPlatinumFeatureTooltip")
              }}
              disabled={!isGoldOrPlatinum}
            >
              {t("locations.locationAssignmentSummary")}
            </TooltipButton>
          )}
        </>
      }
    >
      <Box my={3}>
        <LocationsFilters onSubmit={submitExternalFilter} />
      </Box>
      <Table
        data={data}
        columns={memoColumns}
        onStateChange={handleStateChange}
        paginationState={paginationState}
        paginated={true}
        loading={isFetching}
        autoResetRowState={false}
        cellSize="medium"
        fixedSizeColumnWidths
        onRowClick={handleRowClick}
      />
    </AdminPageLayout>
  );
};

export default Locations;
